import { StatusType } from "@corp-ais/omniark-ui";
import { EnrichmentListPriceItem } from "@app/shared/models/enrichment-detail.model";
import { LifecycleStatus } from "@app/shared/models/enrichment-list.model";
import {
  canonicalStatus,
  packageStatusView,
} from "@app/shared/models/package-status.model";
import {
  EnrichmentPriceRow,
  PriceRowKey,
  PricesByOffering,
} from "./enrichment-list.types";
import { formatLastUpdate } from "./last-update.format";

/** Format a price validity timestamp as "DD/MM/YYYY - HH:MM" (viewer's zone).
 *  Empty / unparseable → "". Last Update stamps use `formatLastUpdate`. */
function formatDateTime(s: string | undefined): string {
  if (!s) return "";
  const d = new Date(s);
  if (Number.isNaN(d.getTime())) return "";
  return d
    .toLocaleString("en-GB", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    })
    .replace(", ", " - ");
}

export function rowKey({ offeringId, priceId }: PriceRowKey): string {
  return `${offeringId}::${priceId}`;
}

/** `<enr-table [trackByFn]>`: the table matches `[checked]` rows and tracks
 *  its row loop through this, so two rows of one shared price stay apart. */
export const trackByRowKey = (_: number, row: EnrichmentPriceRow): string =>
  row.key;

const STATUS_TYPE: Record<LifecycleStatus, StatusType> = {
  inDesign: "indesign",
  designed: "designed",
  inTest: "intest",
  active: "active",
  inActive: "inactive",
  launched: "launched",
  retired: "retired",
  obsolete: "obsolete",
  rejected: "rejected",
  deleted: "deleted",
};

// An un-scoped read lists the master POP's own channel on every row, so it
// carries no information there. Dropping it leaves an unenriched price with
// nothing, which reads "Not enriched" as the scoped read does.
const MASTER_CHANNEL_NAMES = new Set(["ALL", "All"]);

function displayEnrichedChannels(
  raw: string[],
  channelSelected: boolean,
): string[] {
  return channelSelected
    ? raw
    : raw.filter((name) => !MASTER_CHANNEL_NAMES.has(name));
}

export interface PriceRowContext {
  channelSelected: boolean;
  /** Anchors the relative "2h ago" times; pass one value per page. */
  now: number;
}

/** One `GET /enrichment/price` item → one list row. */
export function toPriceRow(
  item: EnrichmentListPriceItem,
  { channelSelected, now }: PriceRowContext,
): EnrichmentPriceRow {
  const offeringId = item.offering?.id ?? "";
  const priceId = item.id;
  const enrichedChannels = nonEmpty(item.enrichedChannels);
  // Under a channel scope an empty enrichedChannels means the row resolved to
  // the master POP. Some BFF builds send the catalog's status for such a row.
  const onChannel = channelSelected && enrichedChannels.length > 0;
  const notEnriched = channelSelected && !onChannel;
  const status: LifecycleStatus | undefined = notEnriched
    ? "inDesign"
    : canonicalStatus(item.lifecycleStatus);
  const displayName = item.name?.trim() ?? "";
  const title = item.systemName?.trim() || displayName || "Untitled price";
  const start = formatDateTime(item.validFor?.startDateTime) || undefined;
  const end = formatDateTime(item.validFor?.endDateTime) || undefined;
  return {
    key: rowKey({ offeringId, priceId }),
    offeringId,
    priceId,
    onChannel,
    notEnriched,
    status,
    statusType: status ? STATUS_TYPE[status] : "pending",
    offeringName: item.offering?.name?.trim() ?? "",
    offeringVersion: item.offering?.version?.trim() ?? "",
    priceVersion: item.version?.trim() ?? "",
    productLine: item.offering?.productLine?.trim() ?? "",
    productType: item.offering?.productType?.trim() ?? "",
    offerClassification: item.offering?.offerClassificationName?.trim() ?? "",
    packageStatus: packageStatusView(item.offering?.lifecycleStatus),
    title,
    subtitle: displayName && displayName !== title ? displayName : "",
    productCode: item.productCode?.trim() || undefined,
    productNumber: item.productNumber?.trim() || undefined,
    amount: formatAmount(item.price),
    priceType: item.priceType?.trim() || undefined,
    shelves: (item.shelves ?? []).filter((s) => !!s?.id && !!s.name),
    salesChannels: nonEmpty(item.salesChannels),
    enrichedChannels: displayEnrichedChannels(
      enrichedChannels,
      channelSelected,
    ),
    validFor: start || end ? { start, end } : undefined,
    deleteDate: formatDateTime(item.deleteDate) || undefined,
    lastUpdate: formatLastUpdate(item.updatedAt, item.updatedBy, now),
    raw: item,
  };
}

/** Each price once, in first-seen order, through its first row on the
 *  channel when it has one: only that row carries the price's status there. */
export function pricesOf(
  rows: readonly EnrichmentPriceRow[],
): EnrichmentPriceRow[] {
  const byPrice = new Map<string, EnrichmentPriceRow>();
  for (const row of rows) {
    const held = byPrice.get(row.priceId);
    if (!held || (!held.onChannel && row.onChannel)) {
      byPrice.set(row.priceId, row);
    }
  }
  return [...byPrice.values()];
}

export function toPriceRows(
  items: readonly EnrichmentListPriceItem[],
  ctx: PriceRowContext,
): EnrichmentPriceRow[] {
  return items.map((item) => toPriceRow(item, ctx));
}

function formatAmount(price: EnrichmentListPriceItem["price"]): string {
  const value = typeof price?.value === "number" ? price.value : 0;
  if (value <= 0) return "-";
  return `${value.toLocaleString()} ${price?.unit?.trim() || "THB"}`;
}

function nonEmpty(names: readonly string[] | undefined): string[] {
  return names?.filter((n): n is string => !!n) ?? [];
}

/** Each distinct price once, under the package of its first row: the price
 *  overlay is keyed by (price, channel), so a price shared by two packages
 *  must be written through one of them only. */
export function groupByOffering(
  rows: readonly PriceRowKey[],
): PricesByOffering {
  const grouped = new Map<string, [string, ...string[]]>();
  const seen = new Set<string>();
  for (const { offeringId, priceId } of rows) {
    if (!offeringId || !priceId || seen.has(priceId)) continue;
    seen.add(priceId);
    const ids = grouped.get(offeringId);
    if (ids) ids.push(priceId);
    else grouped.set(offeringId, [priceId]);
  }
  return grouped;
}
